import React, { useEffect, useRef } from 'react'
import { useHookstate } from '@hookstate/core';
import { useParams } from 'react-router-dom';
import { useLang } from '~/hooks/useLang';
import { getDispatchByIdService } from '../services';
import { dispatchDefault } from '../stores/DispatchState';
import { parseDataDispatch } from '../utils/parsers';
import type { vehicleHistoryProps } from '../interfaces/IDispacthVehicle';
import useDispatchRegistrationPersonState from '../stores/DispatchRegistrationPersonState';
import { getContributersDevicesFromOccurrenceService } from '~/features/Occurrence/services';
import useDispatchFormBuilderState, { formBuilderDefaultState } from '../stores/useDispatchFormBuilderState';
import { Button, CircularProgress, Container, Grid, Typography } from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import RealoadButtonPrint from '../components/Print/RealodButtonPrint';
import HeaderPrintForm from '../components/PrintForm/HeaderPrintForm';
import type { IFormBuilder } from '../interfaces/IDispatchFormBuilder';
import TabPrint from '../components/Print/TabPrint';
import OccurrenceFormData from '../components/PrintForm/OccurrenceFormData';
import CrewCompositionForm from '../components/PrintForm/CrewCompositionForm';
import { type peopleHistoryProps } from '../components/Print/InvolvedInTheOccurrencePrint';
import FieldsPrintForm from '../components/PrintForm/FieldsPrintForm';
import dayjs from 'dayjs';
import SignatureForm from '../components/PrintForm/SignatureForm';
import PersonPrintForm from '../components/PrintForm/PersonPrintForm';

interface paramsProps {
  readonly dispatchId: string
  readonly itemId: string
  readonly formId: string
}

export default function PrintForm() {
  const { translate } = useLang();
  const { dispatchId, itemId, formId }: paramsProps = useParams();
  const { viewPerson } = useDispatchRegistrationPersonState();
  const { getFormWithValues } = useDispatchFormBuilderState();
  const componentRef = useRef(null);
  const loading = useHookstate(true);
  const showPrint = useHookstate(true)
  const dispatchResponse = useHookstate(dispatchDefault);
  const dispatchedVehiclesPrintResponse = useHookstate<vehicleHistoryProps[]>([]);
  const formWithValuesResponse = useHookstate<IFormBuilder>(formBuilderDefaultState);
  const personResponse = useHookstate<peopleHistoryProps | null>(null);

  useEffect(() => {
    const loadPrintData = async () => {
      if (!dispatchId || !itemId || !formId) {
        return;
      }

      try {
        loading.set(true);

        const [
          dispatchData,
          devicesData,
          personData,
          formData
        ] = await Promise.all([
          getDispatchByIdService(dispatchId),
          getContributersDevicesFromOccurrenceService(dispatchId),
          viewPerson(String(itemId)),
          getFormWithValues({ itemId: Number(itemId), formId: Number(formId) })
        ]);

        dispatchResponse.set(parseDataDispatch(dispatchData));
        dispatchedVehiclesPrintResponse.set(devicesData);
        personResponse.set(personData);
        formWithValuesResponse.set(formData);

      } catch (error) {
        console.error('Erro ao carregar dados para impressão:', error);
        showPrint.set(false);
      } finally {
        loading.set(false);
      }
    };

    loadPrintData();
  }, [dispatchId, itemId, formId]);


  const handlePrint = useReactToPrint({
    documentTitle: `${formWithValuesResponse.get()?.name} - ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`,
    content: () => componentRef.current,
    removeAfterPrint: false,
  });

  return (
    !loading.get() && showPrint.get() ?
      <div>
        <Button onClick={handlePrint}>
          {'Imprimir'}
        </Button>
        <div ref={componentRef} className="print-container" style={{ margin: "0", padding: "0", display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          <div className="page-break" />

          <div className="print-content" style={{ flex: 1 }}>
            <HeaderPrintForm
              formInfos={formWithValuesResponse.get()}
              dispatchInfo={dispatchResponse.get()}
            />

            <TabPrint
              title={`${translate('Occurrence Data')}${dispatchResponse.get()?.type?.name ? ` - ${dispatchResponse.get()?.type?.name}` : ''}${dispatchResponse.get()?.subtype?.name ? ` ${dispatchResponse.get()?.subtype?.name}` : ''}`}
            >
              <OccurrenceFormData
                dispatchResponse={dispatchResponse.get()}
              />
            </TabPrint>

            <TabPrint
              title={translate('Crew Composition')}>
              <CrewCompositionForm dispatchedVehiclesPrintResponse={dispatchedVehiclesPrintResponse.get()} />
            </TabPrint>

            <TabPrint
              title={'Envolvidos na Ocorrência'}>
              <PersonPrintForm InvolvedInTheOccurrencePrintResponse={personResponse.get()} />
            </TabPrint>


            <TabPrint
              style={{ pageBreakBefore: 'always' }}
              title={'Formulário da ocorrência'}>
              <FieldsPrintForm formWithValuesResponse={formWithValuesResponse.get()} />
            </TabPrint>

            <SignatureForm />
          </div>


        </div>
      </div>
      : !showPrint.get() ?
        <RealoadButtonPrint
          message={translate('An error occurred while trying to generate the printout')}
          reloadInfo={() => window.location.reload()}
        />
        :
        <Container>
          <Grid container xs={12} >
            <Grid
              item
              xs={12}
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
              height={'calc(100vh - 80px)'}
            >
              <CircularProgress />
            </Grid>
          </Grid>
        </Container>
  )
}